import { useCallback, useEffect, useMemo, useState } from 'react';
import PatientLayout from '@/components/patient/PatientLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { csrfFetch } from '@/lib/csrf';
import { logError } from '@/lib/logger';
import type { PageProps } from './Show';
import type { StructuredReview, DeterministicData } from '@/components/patient-review/types';
import { StaleDataBanner } from '@/components/patient-review/StaleDataBanner';
import { RiskFlagsAlert } from '@/components/patient-review/RiskFlagsAlert';
import { ReviewOverview } from '@/components/patient-review/ReviewOverview';
import { ReviewTimeline } from '@/components/patient-review/ReviewTimeline';
import { ReviewThemes } from '@/components/patient-review/ReviewThemes';
import { ReviewKeyEvents } from '@/components/patient-review/ReviewKeyEvents';
import { ReviewObservations } from '@/components/patient-review/ReviewObservations';
import { EngagementMetrics } from '@/components/patient-review/EngagementMetrics';

type ReviewPreset = 'last_7_days' | 'last_14_days' | 'custom';

type CachedReview = {
  window_start: string;
  window_end: string;
  llm: StructuredReview;
  deterministic: DeterministicData;
};

type WeeklyReviewProps = PageProps & {
  cachedReview: CachedReview | null;
  stale: boolean;
};

type ReviewState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  llm: StructuredReview | null;
  deterministic: DeterministicData | null;
  windowStart: string | null;
  windowEnd: string | null;
  errorMessage: string;
};

const DEFAULT_ERROR = 'Non siamo riusciti a creare il riepilogo. Riprova ora o contatta l\'assistenza.';
const DEFAULT_PRESET: ReviewPreset = 'last_7_days';
const PRESET_VALUES: ReviewPreset[] = ['last_7_days', 'last_14_days', 'custom'];

type ReviewRequestParams = {
  preset: ReviewPreset;
  windowStart?: string;
  windowEnd?: string;
};

function formatDateShort(value?: string | null) {
  if (!value) return '';
  const parts = value.split('-');
  if (parts.length !== 3) return '';
  const [, month, day] = parts;
  if (!month || !day) return '';
  return `${day}/${month}`;
}

function parseReviewParams(): ReviewRequestParams {
  if (typeof window === 'undefined') return { preset: DEFAULT_PRESET };
  const params = new URLSearchParams(window.location.search);
  const presetParam = params.get('preset') as ReviewPreset | null;
  const preset = PRESET_VALUES.includes(presetParam || DEFAULT_PRESET)
    ? (presetParam as ReviewPreset)
    : DEFAULT_PRESET;
  if (preset !== 'custom') return { preset };

  const windowStart = params.get('window_start') || '';
  const windowEnd = params.get('window_end') || '';
  if (!windowStart || !windowEnd) return { preset: DEFAULT_PRESET };
  return { preset, windowStart, windowEnd };
}

export default function WeeklyReview(props: WeeklyReviewProps) {
  const { patient, stats, charts, cachedReview, stale } = props;
  const hasCached = cachedReview !== null && cachedReview.llm !== null;

  const [state, setState] = useState<ReviewState>(() => {
    if (hasCached) {
      return {
        status: 'success',
        llm: cachedReview.llm,
        deterministic: cachedReview.deterministic,
        windowStart: cachedReview.window_start,
        windowEnd: cachedReview.window_end,
        errorMessage: '',
      };
    }
    return {
      status: 'idle',
      llm: null,
      deterministic: null,
      windowStart: null,
      windowEnd: null,
      errorMessage: '',
    };
  });
  const [isStale, setIsStale] = useState(stale);
  const reviewParams = useMemo(() => parseReviewParams(), []);

  const windowLabel = useMemo(() => {
    if (!state.windowStart || !state.windowEnd) return '';
    const start = formatDateShort(state.windowStart);
    const end = formatDateShort(state.windowEnd);
    if (!start || !end) return '';
    return `Periodo ${start} - ${end}`;
  }, [state.windowStart, state.windowEnd]);

  const fetchReview = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      status: 'loading',
      errorMessage: '',
    }));

    try {
      const body: Record<string, string> = { preset: reviewParams.preset };
      if (reviewParams.preset === 'custom') {
        if (reviewParams.windowStart) body.window_start = reviewParams.windowStart;
        if (reviewParams.windowEnd) body.window_end = reviewParams.windowEnd;
      }

      const response = await csrfFetch(`/therapists/patients/${patient.id}/weekly_review`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = typeof data?.message === 'string' ? data.message : DEFAULT_ERROR;
        throw new Error(message);
      }

      if (!data?.llm) {
        throw new Error('Risposta non valida dal server.');
      }

      setState({
        status: 'success',
        llm: data.llm as StructuredReview,
        deterministic: (data.deterministic as DeterministicData) || null,
        windowStart: data.window_start ?? null,
        windowEnd: data.window_end ?? null,
        errorMessage: '',
      });
      setIsStale(false);
    } catch (error) {
      logError('Weekly review generation failed', error);
      setState((prev) => ({
        ...prev,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : DEFAULT_ERROR,
      }));
    }
  }, [patient.id, reviewParams]);

  useEffect(() => {
    if (!hasCached) {
      void fetchReview();
    }
  }, [fetchReview, hasCached]);

  const isLoading = state.status === 'loading';
  const hasData = state.status === 'success' && state.llm !== null;

  return (
    <PatientLayout
      patient={patient}
      stats={stats}
      charts={charts}
      activeTab="dashboard"
      showWeeklyReviewCta={false}
    >
      <div className="space-y-4">
        {/* Row 1: Header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold">Riepilogo settimanale</h1>
          {windowLabel ? (
            <Badge variant="outline">{windowLabel}</Badge>
          ) : null}
        </div>

        {/* Row 2: Stale Banner */}
        {isStale && hasData ? (
          <StaleDataBanner onRegenerate={fetchReview} isLoading={isLoading} />
        ) : null}

        {/* Loading state */}
        {isLoading && !hasData ? (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/6" />
                <Skeleton className="h-4 w-3/6" />
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Error state */}
        {state.status === 'error' && !hasData ? (
          <Card semantic="destructive">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">{state.errorMessage || DEFAULT_ERROR}</p>
                <Button variant="outline" onClick={fetchReview} isLoading={isLoading}>
                  Riprova
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {hasData && state.llm ? (
          <>
            {/* Row 3: Risk Flags */}
            <RiskFlagsAlert flags={state.llm.risk_flags} />

            {/* Row 4: Engagement + Overview (2 colonne) */}
            <div className={`grid gap-4 ${state.deterministic?.engagement ? 'grid-cols-1 md:grid-cols-[240px_1fr]' : 'grid-cols-1'}`}>
              {state.deterministic?.engagement ? (
                <EngagementMetrics engagement={state.deterministic.engagement} />
              ) : null}
              <ReviewOverview overview={state.llm.overview} />
            </div>

            {/* Row 5: Timeline orizzontale */}
            {state.deterministic?.timeline ? (
              <ReviewTimeline items={state.deterministic.timeline} windowStart={state.windowStart} windowEnd={state.windowEnd} />
            ) : null}

            {/* Row 6: Temi + Osservazioni (2 colonne) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ReviewThemes themes={state.llm.themes} />
              <ReviewObservations observations={state.llm.observations} />
            </div>

            {/* Row 7: Eventi chiave */}
            <ReviewKeyEvents events={state.llm.key_events} />
          </>
        ) : null}
      </div>
    </PatientLayout>
  );
}
